<template>
	<!-- The map's height is its parent's: Stations.vue sizes the page, this
	     component fills it. -->
	<div class="station-map">
		<mgl-map
			:map-style="mapStyleURL(style)"
			height="100%"
			@map:load="onMapLoad"
		>
			<!-- The district boundary is the frame: the map fits it once, when
			     it arrives, and never again — an operator who has panned or
			     zoomed is not yanked back by a later re-render. -->
			<SourceServiceArea
				:show-aoi-boundary="false"
				@service-area-loaded="onServiceAreaLoaded"
			/>

			<!-- The parcels, with the existing cyan outline for the parcel the
			     add-station flow picked. -->
			<SourceParcel :selected-parcel-external-id="selectedParcelExternalID" />

			<!-- The district's stations, one MVT feature each at its latest
			     location version. Retired stations are in the same tile, so the
			     "show retired" toggle is the layer's filter — never a fetch. -->
			<mgl-vector-source
				v-if="stationTiles.length > 0"
				source-id="trap-stations"
				:tiles="stationTiles"
			>
				<mgl-circle-layer
					layer-id="station-circles"
					source-layer="service-area-trap-station"
					:filter="stationFilter"
					:paint="stationPaint"
				/>
			</mgl-vector-source>
		</mgl-map>

		<MapStyleSwitcher v-model="style" />
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
	MglCircleLayer,
	MglMap,
	MglVectorSource,
	useMap,
	type MglEvent,
} from "vue-maplibre-gl";
import type { GeoJSON } from "geojson";
import type { MapMouseEvent } from "maplibre-gl";

import { useSessionStore } from "@/store/session";
import { boundsFromGeoJSON, mapStyleURL } from "@/map/util";
import { ALIDADE_SMOOTH } from "@/map/constants";
import MapStyleSwitcher from "@/map/MapStyleSwitcher.vue";
import SourceParcel from "@/map/SourceParcel.vue";
import SourceServiceArea from "@/map/SourceServiceArea.vue";
import { TRAP_STATION_COLOR } from "@/lib/mapReviewLayers";
import {
	parcelHitFromFeatures,
	stationCircleFilter,
	stationFeatureID,
	stationTilesURL,
	type StationParcelClick,
} from "@/lib/stationMap";

interface Props {
	/** Whether retired stations are drawn. A filter on the station layer, so
	 * switching it never re-reads the tiles. */
	showRetired?: boolean;
	/** The station to emphasise, if any. */
	selectedStationID?: number | null;
	/** The parcel to outline, by its assessor id. */
	selectedParcelExternalID?: string | null;
	/** Whether a click chooses a parcel (the add-station flow). While picking,
	 * the canvas shows a crosshair and a click that hits no parcel still
	 * reports its point, so the flow can offer to create a site there. */
	picking?: boolean;
	/** A geometry to fit the camera to. Each new object fits again, so the
	 * caller sets the parcel it picked and the station it selected. */
	fitGeometry?: GeoJSON.GeoJSON | null;
}

const props = withDefaults(defineProps<Props>(), {
	showRetired: false,
	selectedStationID: null,
	selectedParcelExternalID: null,
	picking: false,
	fitGeometry: null,
});

const emit = defineEmits<{
	(e: "station-click", id: number): void;
	(e: "parcel-click", hit: StationParcelClick): void;
}>();

const session = useSessionStore();
const mapInstance = useMap();

/** The base style. The switcher swaps smooth ↔ satellite for headquarters
 * recognising a property in imagery. */
const style = ref(ALIDADE_SMOOTH);

const stationTiles = computed(() => stationTilesURL(session.urls?.tegola));

/** `active` read as a boolean, whatever the tile encoded it as — the same
 * reading `stationCircleFilter` makes. */
const ACTIVE: unknown[] = ["to-boolean", ["get", "active"]];
const STATION_LAYERS = ["station-circles"];

const stationFilter = computed(
	() => stationCircleFilter({ showRetired: props.showRetired }) as any,
);

/** Active stations are the district's own teal, retired ones are drawn muted
 * and smaller so the live network reads first. The selected station carries the
 * same cyan ring the parcel selection uses — selection is paint, not a filter,
 * because a filter naming an id would hide every other station. */
const stationPaint = computed(() => {
	const selected = props.selectedStationID ?? -1;
	const isSelected: unknown[] = ["==", ["get", "id"], selected];
	return {
		"circle-radius": ["case", isSelected, 10, ["case", ACTIVE, 8, 6]],
		"circle-color": ["case", ACTIVE, TRAP_STATION_COLOR, "#adb5bd"],
		"circle-opacity": ["case", ACTIVE, 1, 0.65],
		"circle-stroke-color": ["case", isSelected, "#00e5ff", "#ffffff"],
		"circle-stroke-width": ["case", isSelected, 3, 1.5],
	} as any;
});

// The district boundary, held until both it and the map are ready. `fitted`
// makes the fit a one-time act: the boundary is emitted once, but the map may
// be re-styled or re-rendered later, and the camera must stay where the
// operator put it.
const districtGeometry = ref<GeoJSON.GeoJSON | null>(null);
const fitted = ref(false);

function fitDistrict() {
	const map = mapInstance.map;
	if (!map || fitted.value || !districtGeometry.value) return;
	const bounds = boundsFromGeoJSON(districtGeometry.value);
	if (!bounds) return;
	map.fitBounds(bounds, { padding: 30, duration: 0 });
	fitted.value = true;
}

function onServiceAreaLoaded(geometry: GeoJSON.GeoJSON | null) {
	districtGeometry.value = geometry;
	fitDistrict();
}

function applyPickingCursor() {
	const canvas = mapInstance.map?.getCanvas();
	if (canvas) canvas.style.cursor = props.picking ? "crosshair" : "";
}

watch(
	() => props.picking,
	() => applyPickingCursor(),
);

// The camera moves only when the caller asks for it — never on a re-render.
watch(
	() => props.fitGeometry,
	(geometry) => {
		const map = mapInstance.map;
		if (!map || !geometry) return;
		const bounds = boundsFromGeoJSON(geometry);
		if (bounds) map.fitBounds(bounds, { padding: 60, duration: 600 });
	},
);

/**
 * One click, in priority order: a station (which navigation to the station's
 * page follows), then the parcel under the point. While picking, stations are
 * skipped — the operator is choosing a place, and an existing station sitting
 * on the parcel they want must not send them off the flow.
 */
function onClick(event: MapMouseEvent) {
	const map = mapInstance.map;
	if (!map) return;

	if (!props.picking) {
		const layers = STATION_LAYERS.filter((layer) => map.getLayer(layer));
		if (layers.length > 0) {
			const hit = map.queryRenderedFeatures(event.point, { layers })[0];
			const stationID = hit ? stationFeatureID(hit) : null;
			if (stationID !== null) {
				emit("station-click", stationID);
				return;
			}
		}
	}

	const { lng, lat } = event.lngLat;
	const parcel = map.getLayer("parcel-fill")
		? parcelHitFromFeatures(
				map.queryRenderedFeatures(event.point, { layers: ["parcel-fill"] }),
			)
		: null;
	if (parcel) {
		emit("parcel-click", { ...parcel, lng, lat });
		return;
	}
	// A click with no parcel — below the parcels' z14, or a gap in coverage —
	// still names a point, which is all the add-station flow needs to offer a
	// reverse-geocoded site.
	if (props.picking) {
		emit("parcel-click", { external_id: "", region: "", lng, lat });
	}
}

function onMapLoad(event: MglEvent<"load">) {
	event.map.on("click", onClick);
	applyPickingCursor();
	fitDistrict();
}
</script>

<style scoped>
.station-map {
	position: relative;
	height: 100%;
	width: 100%;
}
</style>
